import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

/**
 * Formats a date to AWS DateTime format (ISO 8601)
 * @param date - The date to format
 * @returns The formatted date string
 */
export const formatAWSDateTime = (date: dayjs.Dayjs | Date | string): string => {
  return dayjs(date).toISOString();
};

/**
 * Gets the date from n weeks ago
 * @param weeks - Number of weeks ago
 * @param fromDate - The starting date (defaults to current date)
 * @returns The date from n weeks ago
 */
export const getDateWeeksAgo = (weeks: number, fromDate?: dayjs.Dayjs | Date | string): dayjs.Dayjs => {
  const startDate = fromDate ? dayjs(fromDate) : dayjs();
  return startDate.subtract(weeks, 'week');
};

/**
 * Formats a date to a human-readable format
 * @param date - The date to format
 * @param format - The format to use (defaults to 'YYYY-MM-DD HH:mm:ss')
 * @returns The formatted date string
 */
export const formatDate = (date: dayjs.Dayjs | Date | string, format = 'YYYY-MM-DD HH:mm:ss'): string => {
  return dayjs(date).format(format);
};

/**
 * Formats a date to a relative time format (e.g., "2 days ago")
 * @param date - The date to format
 * @returns The relative time string
 */
export const formatRelativeTime = (date: dayjs.Dayjs | Date | string): string => {
  return dayjs(date).fromNow();
};

/**
 * Checks if a date is before another date
 * @param date1 - The first date
 * @param date2 - The second date (defaults to current date)
 * @returns True if date1 is before date2
 */
export const isDateBefore = (date1: dayjs.Dayjs | Date | string, date2?: dayjs.Dayjs | Date | string): boolean => {
  const secondDate = date2 ? dayjs(date2) : dayjs();
  return dayjs(date1).isBefore(secondDate);
};

export const dateTimezoneConversion = (date: string, timezone: string) => {
  if (timezone && typeof timezone === 'string' && !timezone?.includes("null")) {
    return dayjs.tz(date?.slice(0, -6), timezone).format();
  }
  return dayjs(date).format();
}

export default {
  formatAWSDateTime,
  getDateWeeksAgo,
  formatDate,
  formatRelativeTime,
  isDateBefore,
};